import { agencyIdFromPath } from '../../../features/auth/agencyAccess';
import { useShigoSession } from '../../../lib/useShigoSession';
import { resolveSectionBreadcrumbs } from './breadcrumbNavigation';
import {
  publisherIdFromPath,
  resolveOwnedDashboardContext,
  ShigoDashboardContext,
} from '../../../features/auth/dashboardDestination';
import { useShigoPermissions } from '../../../features/auth/useShigoPermissions';
import { useNetworkFeatures } from '../../../features/auth/useNetworkFeatures';
import { Fragment, useSyncExternalStore } from 'react';
import { Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
  Skeleton,
  cn,
} from '@shigo-ui/ui';
import type { BreadcrumbSegment } from '../../../lib/breadcrumbs';
import { dashboardT } from '../../../lib/messages';
import {
  cachedBreadcrumbName,
  useBreadcrumbs,
  type BreadcrumbQueryContext,
} from './breadcrumbQueries';

export function Breadcrumbs({ rootTitle }: { rootTitle: string }) {
  const { crumbs: originalCrumbs, context } = useBreadcrumbs(rootTitle);
  const { session } = useShigoSession();
  const { can } = useShigoPermissions();
  const { isFeatureAllowed } = useNetworkFeatures();
  const publisherId = publisherIdFromPath(context.pathname);
  const agencyId = agencyIdFromPath(context.pathname);
  let dashboardContext = session ? resolveOwnedDashboardContext(session.user) : null;
  if (publisherId) dashboardContext = ShigoDashboardContext.Publisher;
  if (agencyId) dashboardContext = ShigoDashboardContext.Agency;
  const crumbs = resolveSectionBreadcrumbs(
    originalCrumbs,
    { dashboardContext, can, isFeatureAllowed },
    {
      ...context.params,
      ...(publisherId ? { publisherId } : {}),
      ...(agencyId ? { agencyId } : {}),
    },
  );
  const collapsed = (index: number) => index < crumbs.length - 2;
  return (
    <Breadcrumb
      aria-label={dashboardT('nav.breadcrumb.label')}
      className="min-w-0 flex-1 overflow-hidden"
    >
      <BreadcrumbList className="min-w-0 flex-nowrap">
        {crumbs.map((crumb, index) => (
          <Fragment key={crumb.path}>
            {index > 0 ? (
              <BreadcrumbSeparator
                className={cn((collapsed(index) || collapsed(index - 1)) && 'hidden md:flex')}
              />
            ) : null}
            <BreadcrumbItem
              className={cn(
                'min-w-0',
                collapsed(index) && 'hidden md:inline-flex',
                index === crumbs.length - 1 && 'max-w-[60%] shrink-0',
              )}
            >
              {crumb.to ? (
                <BreadcrumbLink asChild>
                  <Link
                    to={crumb.to}
                    search={crumb.search ?? {}}
                    activeOptions={{ exact: true }}
                    className="focus-visible:ring-ring truncate rounded-sm focus-visible:ring-2 focus-visible:outline-none"
                  >
                    <BreadcrumbLabel crumb={crumb} context={context} />
                  </Link>
                </BreadcrumbLink>
              ) : (
                <span
                  aria-current={index === crumbs.length - 1 ? 'page' : undefined}
                  className={cn('truncate', index === crumbs.length - 1 && 'text-foreground')}
                >
                  <BreadcrumbLabel crumb={crumb} context={context} />
                </span>
              )}
            </BreadcrumbItem>
          </Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

function BreadcrumbLabel({
  crumb,
  context,
}: {
  crumb: BreadcrumbSegment;
  context: BreadcrumbQueryContext;
}) {
  const client = useQueryClient();
  const cache = client.getQueryCache();
  const label = useSyncExternalStore(
    (onChange) => cache.subscribe(onChange),
    () =>
      crumb.entity ? cachedBreadcrumbName(client, crumb.entity, context, crumb.label) : crumb.label,
  );
  if (label !== null && !crumb.loading) return label;
  return (
    <Skeleton
      role="status"
      aria-label={dashboardT('nav.breadcrumb.loading')}
      className="inline-block h-4 w-28 max-w-full align-middle"
    />
  );
}
